import { Card, Checkbox, Space, Typography } from 'antd';
import ImportTitle from './ImportTitle';
import React from "react";

const { Paragraph } = Typography;

const ImportInfoStep = ({ accountData, selectedFields, onFieldSelection, supplierOrg }) => {
    const CheckboxField = ({ checked, onChange, children, style = {} }) => (
        <div className="flex items-start gap-4 flex-row" style={{ ...style }}>
            <Space>
                <Checkbox checked={checked} onChange={onChange} />
            </Space>
            <div style={{ flex: 1 }}>{children}</div>
        </div>
    );

    const hasAnyData = TB.stripHtml(accountData?.about)?.length || accountData?.website ||
        accountData?.is_black_owned || accountData?.is_woman_owned ||
        accountData?.profile_links?.length || accountData?.locations?.length;

    // Calculate which agency info fields are available
    const agencyInfoFields = [];
    if (TB.stripHtml(accountData?.about)?.length > 0) agencyInfoFields.push('about');
    if (accountData?.website) agencyInfoFields.push('website');
    if (accountData?.is_black_owned === true || accountData?.is_black_owned === 1) agencyInfoFields.push('is_black_owned');
    if (accountData?.is_woman_owned === true || accountData?.is_woman_owned === 1) agencyInfoFields.push('is_woman_owned');

    const hasMultipleAgencyInfoFields = agencyInfoFields.length > 1;

    return (
        <div className="py-6">
            <ImportTitle
                title="Agency Info"
                description="Select the information items you'd like to import to your profile"
            />

            {!hasAnyData && (
                <div className="text-center py-8 text-gray-500">
                    No info items found to import
                </div>
            )}

            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                {/* Agency Info */}
                {agencyInfoFields.length > 0 && (
                    <Card title={
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fas fa-info-circle" />
                                <span>Agency Info</span>
                            </div>
                            {hasMultipleAgencyInfoFields && (
                                <Space>
                                    <Checkbox
                                        checked={agencyInfoFields.every(field => selectedFields?.[field])}
                                        onChange={(e) => {
                                            agencyInfoFields.forEach(field => {
                                                onFieldSelection(field, e.target.checked);
                                            });
                                        }}
                                    >
                                        <span className="text-xs text-gray-500">Select All</span>
                                    </Checkbox>
                                </Space>
                            )}
                        </div>
                    }>
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            {/* About */}
                            {accountData?.about && (
                                <CheckboxField
                                    checked={selectedFields?.about}
                                    onChange={(e) => onFieldSelection('about', e.target.checked)}
                                >
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <i className="fas fa-align-left text-xs text-gray-400" />
                                            <span className="font-semibold text-sm">About</span>
                                        </div>
                                        <Paragraph style={{ margin: 0 }}>
                                            <span dangerouslySetInnerHTML={{__html: accountData.about}}></span>
                                        </Paragraph>

                                        {/* Show current about if it exists */}
                                        {TB.stripHtml(supplierOrg?.about)?.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <i className="fas fa-exclamation-triangle text-warning text-xs" />
                                                    <span className="text-xs text-warning font-semibold">
                                                        By Importing, your current about summary will be overwritten.
                                                    </span>
                                                </div>
                                                <Paragraph
                                                    style={{
                                                        margin: 0,
                                                        textDecoration: 'line-through',
                                                        fontStyle: 'italic',
                                                        fontWeight: 300,
                                                        opacity: 0.6,
                                                        color: '#6b7280'
                                                    }}
                                                >

                                                    <span dangerouslySetInnerHTML={{ __html: supplierOrg?.about }}/>
                                                </Paragraph>
                                            </div>
                                        )}
                                    </div>
                                </CheckboxField>
                            )}

                            {/* Website */}
                            {accountData?.website && (
                                <CheckboxField
                                    checked={selectedFields?.website}
                                    onChange={(e) => onFieldSelection('website', e.target.checked)}
                                >
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <i className="fas fa-globe text-xs text-gray-400" />
                                            <span className="font-semibold text-sm">Website</span>
                                        </div>
                                        <a href={accountData.website} target="_blank" rel="noopener noreferrer" className="btn-link-info underline text-sm">
                                            {accountData.website}
                                        </a>
                                    </div>
                                </CheckboxField>
                            )}

                            {/* Black Owned */}
                            {accountData?.is_black_owned === true && (
                                <CheckboxField
                                    checked={selectedFields?.is_black_owned}
                                    onChange={(e) => onFieldSelection('is_black_owned', e.target.checked)}
                                >
                                    <div className="flex items-center gap-2">
                                        <i className="fas fa-check-circle text-xs text-success" />
                                        <span className="font-semibold text-sm">Black Owned Business</span>
                                    </div>
                                </CheckboxField>
                            )}

                            {/* Woman Owned */}
                            {accountData?.is_woman_owned === true && (
                                <CheckboxField
                                    checked={selectedFields?.is_woman_owned}
                                    onChange={(e) => onFieldSelection('is_woman_owned', e.target.checked)}
                                >
                                    <div className="flex items-center gap-2">
                                        <i className="fas fa-check-circle text-xs text-success" />
                                        <span className="font-semibold text-sm">Woman Owned Business</span>
                                    </div>
                                </CheckboxField>
                            )}
                        </Space>
                    </Card>
                )}

                {/* Profile Links */}
                {accountData?.profile_links?.length > 0 && (
                    <Card title={
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fas fa-link" />
                                <span>Profile Links</span>
                            </div>
                            <Space>
                                <Checkbox
                                    checked={accountData.profile_links.every((_, idx) => selectedFields?.profile_links?.[idx])}
                                    onChange={(e) => {
                                        const newValue = {};
                                        accountData.profile_links.forEach((_, idx) => {
                                            newValue[idx] = e.target.checked;
                                        });
                                        onFieldSelection('profile_links', newValue);
                                    }}
                                >
                                    <span className="text-xs text-gray-500">Select All</span>
                                </Checkbox>
                            </Space>
                        </div>
                    }>
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            {accountData.profile_links.map((link, index) => (
                                <CheckboxField
                                    key={`link-${index}`}
                                    checked={selectedFields?.profile_links?.[index]}
                                    onChange={(e) => onFieldSelection('profile_links', {
                                        ...(selectedFields?.profile_links || {}),
                                        [index]: e.target.checked
                                    })}
                                >
                                    <div className="flex flex-row items-center gap-1.5">
                                        <i className="fa fa-link text-xxs text-gray-400" />
                                        <a href={link?.url} target="_blank" rel="noopener noreferrer" className="p-0 btn-link-info underline text-xs line-clamp-1">
                                            {link?.url}
                                        </a>
                                    </div>
                                </CheckboxField>
                            ))}
                        </Space>
                    </Card>
                )}

                {/* Locations */}
                {accountData?.locations?.length > 0 && (
                    <Card title={
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fas fa-map-marker-alt" />
                                <span>Locations</span>
                            </div>
                            <Space>
                                <Checkbox
                                    checked={accountData.locations.every((_, idx) => selectedFields?.locations?.[idx])}
                                    onChange={(e) => {
                                        const newValue = {};
                                        accountData.locations.forEach((_, idx) => {
                                            newValue[idx] = e.target.checked;
                                        });
                                        onFieldSelection('locations', newValue);
                                    }}
                                >
                                    <span className="text-xs text-gray-500">Select All</span>
                                </Checkbox>
                            </Space>
                        </div>
                    }>
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            {accountData.locations.map((location, index) => (
                                <CheckboxField
                                    key={`location-${index}`}
                                    checked={selectedFields?.locations?.[index]}
                                    onChange={(e) => onFieldSelection('locations', {
                                        ...(selectedFields?.locations || {}),
                                        [index]: e.target.checked
                                    })}
                                >
                                    <div className="flex flex-row items-center gap-1.5">
                                        <p className="m-0">{location.full}</p>
                                    </div>
                                </CheckboxField>
                            ))}
                        </Space>
                    </Card>
                )}
            </Space>
        </div>
    );
};

export default ImportInfoStep;